import Client, {
  AttributeInput,
  Cart,
  CartLineEdge,
  EmbarkCart,
  EmbarkClient,
} from 'shopify-buy';

import { getCookie, setCookie } from './cookie';
import { getAllFromFormData } from '../utils/forms';
import { notifyHandledException } from '../utils/errorLogger';
import { launchShelterSelectionModal } from './shelter-selector';
import { launchBreederSelectionModal } from './breeder-selector';
import { removeAutoAppliedDiscountIfCookieDiscountIsPresent } from './discount-code';

export const CART_SESSION_COOKIE_NAME = 'embark_cart_session_id';
export const CART_SESSION_COOKIE_CREATED_AT = 'embark_cart_session_created_at';

const STOREFRONT_API_STORE_URL = process.env.STOREFRONT_API_STORE_URL || '';
const STOREFRONT_ACCESS_TOKEN =
  process.env.STOREFRONT_API_CLIENT_ACCESS_TOKEN || '';

export const client = Client.buildClient({
  apiVersion: '2024-04',
  domain: (process.env.STOREFRONT_API_STORE_URL || '').replace('https://', ''),
  storefrontAccessToken: process.env.STOREFRONT_API_CLIENT_ACCESS_TOKEN || '',
  language: `${window.Shopify.locale}-${window.Shopify.country}`,
}) as EmbarkClient;

export async function fetchGraphQL(
  query: string,
  variables: any = {}
): Promise<any> {
  const response = await fetch(
    `${STOREFRONT_API_STORE_URL}/api/${client.config.apiVersion}/graphql.json`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': `${STOREFRONT_ACCESS_TOKEN}`,
      },
      body: JSON.stringify({ query, variables }),
    }
  );

  const responseData = await response.json();
  if (responseData.errors) {
    // eslint-disable-next-line no-console
    console.error('GraphQL errors:', responseData.errors);
    throw new Error('GraphQL query failed');
  }

  return responseData.data;
}

const CART_QUERY = `#graphql
  query($id: ID!) {
    cart(id: $id) {
      id
      lines(first: 250) {
        edges {
          node {
            id
            quantity
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              compareAtAmountPerQuantity {
                amount
                currencyCode
              }
            }
            merchandise {
              ... on ProductVariant {
                id
                sku
                title
                price {
                  amount
                }
                compareAtPrice {
                  amount
                }
                product {
                  title
                  vendor
                  productType
                  handle
                }
                image {
                  src
                  altText
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
            sellingPlanAllocation {
              sellingPlan {
                id
                name
              }
              priceAdjustments {
                price {
                  amount
                }
              }
            }
            attributes {
              key
              value
            }
            discountAllocations {
              discountedAmount {
                amount
                currencyCode
              }
            }
            __typename
            ... on ComponentizableCartLine {
              lineComponents {
                id
                quantity
                cost {
                  totalAmount {
                    amount
                    currencyCode
                  }
                  subtotalAmount {
                    amount
                    currencyCode
                  }
                  compareAtAmountPerQuantity {
                    amount
                    currencyCode
                  }
                }
                merchandise {
                  __typename
                  ... on ProductVariant {
                    id
                    sku
                    title
                    price {
                      amount
                    }
                    product {
                      title
                      vendor
                      productType
                      handle
                    }
                    image {
                      src
                      altText
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
                sellingPlanAllocation {
                  sellingPlan {
                    id
                    name
                  }
                  priceAdjustments {
                    price {
                      amount
                    }
                  }
                }
              }
            }

          }
        }
      }
      discountCodes {
        code
      }
      attributes {
        key
        value
      }
      note
      checkoutUrl
      cost {
        checkoutChargeAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
        totalAmount {
          amount
          currencyCode
        }
      }
      discountAllocations {
        discountedAmount {
          amount
        }
      }
    }
  }
`;

const CREATE_CART_MUTATION = `
  mutation {
    cartCreate {
      cart {
        id
        checkoutUrl
      }
    }
  }
`;

const ADD_LINE_ITEM_MUTATION = `#graphql
  mutation($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 250) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  sku
                  price {
                    amount
                  }
                  compareAtPrice {
                    amount
                  }
                }
              }
              sellingPlanAllocation {
                sellingPlan {
                  id
                  name
                }
                priceAdjustments {
                  price {
                    amount
                  }
                }
              }

              ... on ComponentizableCartLine {
                lineComponents {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      sku
                    }
                  }
                  sellingPlanAllocation {
                    sellingPlan {
                      id
                      name
                    }
                    priceAdjustments {
                      price {
                        amount
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export async function getOrCreateCart(): Promise<any> {
  const cartId = getCookie(CART_SESSION_COOKIE_NAME);

  if (cartId) {
    const data = await fetchGraphQL(CART_QUERY, { id: cartId });

    if (data.cart) {
      return data.cart;
    }
  }

  const response = await fetchGraphQL(CREATE_CART_MUTATION);
  const cart = response.cartCreate.cart;
  setCookie(CART_SESSION_COOKIE_NAME, cart.id);
  setCookie(CART_SESSION_COOKIE_CREATED_AT, Date.now().toString());
  return cart;
}

function setLoading(isLoading: boolean, event: MouseEvent | SubmitEvent): void {
  const target = getSubmitter(event);

  if (
    target instanceof HTMLAnchorElement ||
    target instanceof HTMLButtonElement
  ) {
    isLoading
      ? target?.setAttribute('data-busy', 'true')
      : target?.removeAttribute('data-busy');
  }

  document.body.setAttribute('data-is-loading', isLoading.toString());
}

export async function addLineItem(
  variantId: string,
  quantity: number,
  sellingPlanId: string | null,
  proceedToCheckout = false,
  customAttributes: any[] = []
): Promise<void> {
  const cart = await getOrCreateCart();

  const line = {
    merchandiseId: variantId,
    quantity,
    sellingPlanId,
    attributes: customAttributes,
  };

  const response = await fetchGraphQL(ADD_LINE_ITEM_MUTATION, {
    cartId: cart.id,
    lines: [line],
  });

  if (response.cartLinesAdd.userErrors.length) {
    notifyHandledException(
      new Error(response.cartLinesAdd.userErrors[0].message)
    );
    return;
  }

  document.dispatchEvent(
    new CustomEvent('cart:add-to-cart', {
      detail: {
        cart,
        variantId,
        quantity,
        sellingPlanId,
        customAttributes,
      },
    })
  );

  if (proceedToCheckout) {
    window.location.href = (cart.checkoutUrl || '').replace(
      'embark-vet.myshopify.com',
      'shop.embarkvet.com'
    );
  } else {
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }
}

export function getDiscountCodeToApply(cart: Cart): string | null | undefined {
  return (
    getCurrentlyAppliedDiscountCode(cart) ||
    window._EMBARKVET_CURRENTLY_APPLIED_DISCOUNT_CODE
  );
}

export function getCurrentlyAppliedDiscountCode(cart: Cart): string | null {
  const discountCodeApplications = cart.discountCodes;
  return discountCodeApplications[0]?.code || null;
}

export const toShopifyGlobalId = (
  id: string,
  obj = 'ProductVariant'
): string => {
  if (id.startsWith(`gid://shopify/${obj}`)) {
    return id;
  }

  return `gid://shopify/${obj}/${id}`;
};

export const toShopifyNumericId = (
  id: string | undefined,
  obj = 'ProductVariant'
): string => {
  if (!id) {
    return '';
  }

  return id.replace(`gid://shopify/${obj}/`, '');
};

document.addEventListener('click', (e) => {
  if (e.target instanceof HTMLElement) {
    window.lastClickedElement = e.target;
  }
});

const getSubmitter = (e: MouseEvent | SubmitEvent): Element | null => {
  const supportsSubmitter =
    'SubmitEvent' in window && 'submitter' in SubmitEvent.prototype;

  if (e instanceof SubmitEvent && supportsSubmitter) {
    return e.submitter;
  }

  const lastClicked = window.lastClickedElement || null;

  if (lastClicked && lastClicked.matches('[type=submit]')) {
    return lastClicked;
  }

  if (document.activeElement?.matches('[type=submit]')) {
    return document.activeElement;
  }

  const target = e.target;

  if (target instanceof HTMLElement || target instanceof SVGElement) {
    return target;
  }

  return null;
};

async function handleAddToCartEvent(
  e: MouseEvent | SubmitEvent
): Promise<void> {
  if (!(e.target instanceof HTMLElement)) {
    return;
  }

  let variantId = e.target.getAttribute('data-add-to-cart');
  let quantity = '1';
  let sellingPlanId = e.target.getAttribute('data-selling-plan');
  let attributes: AttributeInput[] = [];
  let proceedToCheckout = false;
  let isSubmitEvent = false;

  if (
    !('SubmitEvent' in window) &&
    e.target.matches('form.product-form, form.shopify-product-form')
  ) {
    isSubmitEvent = true;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore -- This is to polyfill older browsers that don't support the SubmitEvent or don't have the submitter property.
    e.submitter = getSubmitter(e);
  }

  if ('SubmitEvent' in window && e instanceof SubmitEvent) {
    isSubmitEvent = true;
  }

  if (isSubmitEvent) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const isCartForm = form.matches(
      'form.product-form, form.shopify-product-form'
    );

    if (!isCartForm) {
      return;
    }

    const formData = new FormData(form);
    variantId = toShopifyGlobalId(formData.get('id') as string);
    quantity = (formData.get('quantity') || '1') as string;
    sellingPlanId = formData.get('selling_plan') as string;
    sellingPlanId = sellingPlanId
      ? toShopifyGlobalId(sellingPlanId, 'SellingPlan')
      : null;
    const properties = getAllFromFormData(formData, 'properties');
    attributes = Object.keys(properties).map((key) => ({
      key,
      value: properties[key] as string,
    }));
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore -- this is needed because `submitter` property doesn't exist on all type of events, we are setting it above to polyfill old browsers.
    const submitter = e.submitter;
    proceedToCheckout =
      submitter?.matches('[data-proceed-to-checkout]') || false;

    if (!form.checkValidity()) {
      return;
    }
  }

  if (e.type === 'click') {
    const target = e.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const isAddToCartButton = target.matches('[data-add-to-cart]');
    if (!isAddToCartButton) {
      return;
    }
  }

  e.preventDefault();

  if (!variantId || !quantity) {
    return;
  }

  if (document.body.classList.contains('shelter-program')) {
    const data = await launchShelterSelectionModal();
    if (data) {
      attributes.push({
        key: 'Shelter',
        value: data,
      });
    }
  }

  if (document.body.classList.contains('breeder-program')) {
    const data = await launchBreederSelectionModal();
    if (data) {
      attributes.push({
        key: 'Breeder',
        value: data,
      });
    }
  }

  setLoading(true, e);
  addLineItem(
    variantId,
    parseInt(quantity, 10),
    sellingPlanId,
    proceedToCheckout,
    attributes
  )
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error adding item to cart:', error);
      notifyHandledException(error);
    })
    .finally(() => {
      setLoading(false, e);
    });
}

export const initStorefrontCart = (): void => {
  removeAutoAppliedDiscountIfCookieDiscountIsPresent();
  getOrCreateCart()
    .then((cart) => {
      updateDataLayerCartItems(cart);
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error initializing cart:', error);
      notifyHandledException(error);
    });

  document.addEventListener('click', handleAddToCartEvent);
  document.addEventListener('submit', handleAddToCartEvent);
};

function updateDataLayerCartItems(cart: EmbarkCart): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'cartUpdated',
    cart: {
      items: cart.lines,
    },
  });

  const product_skus = cart.lines?.edges.map(
    (line) => line?.node.merchandise.sku
  );

  // fire remarketing event
  window.dataLayer.push({
    event: 'fireRemarketingTag',
    remarketing_meta: {
      ecomm_pagetype: 'cart',
      ecomm_prodid: product_skus,
      ecomm_totalvalue: cart.estimatedCost?.totalAmount?.amount || 0,
    },
  });

  // push SKU's to dataLayer
  window.dataLayer.push({
    product_skus,
  });
}

export function getLineItemsCount(cart: EmbarkCart | null): number {
  if (!cart) {
    return 0;
  }

  const count = cart.lines?.edges?.reduce(
    (total: number, edge: CartLineEdge) => {
      return total + edge.node.quantity;
    },
    0
  );

  return count || 0;
}
